import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, ArrowRight, ShoppingBag } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { inr } from "@/lib/api";

export default function Cart() {
  const { items, updateQty, removeItem, subtotal } = useCart();
  const navigate = useNavigate();
  const shipping = subtotal >= 999 || subtotal === 0 ? 0 : 79;
  const total = subtotal + shipping;

  if (items.length === 0)
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-6 pt-[68px] lg:pt-[116px]" data-testid="empty-cart">
        <ShoppingBag className="h-12 w-12 text-black/20" />
        <p className="font-display text-2xl font-semibold">Your cart is empty</p>
        <Link
          to="/shop"
          className="rounded-full bg-black px-8 py-3 font-semibold text-white transition-colors hover:bg-[#2F80ED]"
          data-testid="cart-shop-btn"
        >
          Browse Formulas
        </Link>
      </div>
    );

  return (
    <div className="min-h-screen pt-[68px] lg:pt-[116px]" data-testid="cart-page">
      <div className="mx-auto max-w-[1400px] px-5 py-12 sm:px-8">
        <h1 className="font-display text-4xl font-bold tracking-tight sm:text-5xl">Your Cart</h1>

        <div className="mt-10 grid gap-12 lg:grid-cols-[1.6fr_1fr]">
          {/* Items */}
          <div className="divide-y divide-black/10 border-y border-black/10">
            {items.map((it) => (
              <div key={it.product_id} className="flex gap-4 py-6" data-testid={`cart-item-${it.slug}`}>
                <Link to={`/product/${it.slug}`} className="h-28 w-24 shrink-0 overflow-hidden bg-[#F3F4F6]">
                  <img src={it.image} alt={it.name} className="h-full w-full object-cover" />
                </Link>
                <div className="flex flex-1 flex-col justify-between">
                  <div className="flex items-start justify-between gap-4">
                    <Link to={`/product/${it.slug}`} className="font-display text-lg font-semibold leading-tight hover:text-[#2F80ED]">
                      {it.name}
                    </Link>
                    <button
                      onClick={() => removeItem(it.product_id)}
                      className="text-black/40 hover:text-red-500"
                      data-testid={`remove-${it.slug}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center border border-black/15">
                      <button
                        onClick={() => updateQty(it.product_id, it.quantity - 1)}
                        className="flex h-9 w-9 items-center justify-center hover:bg-black hover:text-white"
                        data-testid={`decrease-${it.slug}`}
                      >
                        <Minus className="h-3.5 w-3.5" />
                      </button>
                      <span className="flex h-9 w-9 items-center justify-center font-mono text-sm font-bold">
                        {it.quantity}
                      </span>
                      <button
                        onClick={() => updateQty(it.product_id, it.quantity + 1)}
                        className="flex h-9 w-9 items-center justify-center hover:bg-black hover:text-white"
                        data-testid={`increase-${it.slug}`}
                      >
                        <Plus className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    <span className="font-mono font-bold">{inr(it.price * it.quantity)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Summary */}
          <div className="h-fit border border-black/10 bg-white p-8 lg:sticky lg:top-24">
            <h2 className="font-mono text-xs uppercase tracking-overline text-black/50">Order Summary</h2>
            <div className="mt-6 space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-black/60">Subtotal</span>
                <span className="font-mono font-semibold" data-testid="cart-subtotal">{inr(subtotal)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-black/60">Shipping</span>
                <span className="font-mono font-semibold">{shipping === 0 ? "FREE" : inr(shipping)}</span>
              </div>
              {shipping > 0 && (
                <p className="font-mono text-[11px] text-[#2F80ED]">
                  Add {inr(999 - subtotal)} more for free shipping
                </p>
              )}
            </div>
            <div className="mt-6 flex justify-between border-t border-black/10 pt-6">
              <span className="font-display text-lg font-bold">Total</span>
              <span className="font-mono text-lg font-bold" data-testid="cart-total">{inr(total)}</span>
            </div>
            <button
              onClick={() => navigate("/checkout")}
              className="btn-glow mt-8 flex w-full items-center justify-center gap-2 bg-black py-4 font-semibold text-white transition-colors hover:bg-[#2F80ED]"
              data-testid="checkout-btn"
            >
              Checkout <ArrowRight className="h-4 w-4" />
            </button>
            <p className="mt-4 text-center font-mono text-[10px] uppercase tracking-overline text-black/40">
              Razorpay · UPI · GPay · Paytm
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
